// Epoch 18: Enhanced Admin Dashboard for CSR Campaign Assignment
// Campaign Filters Component - Advanced filtering and search
// Date: July 26, 2025

import React, { useState, useEffect } from 'react';
import { debounce } from '../../utils/csrAssignment';

export interface FilterState {
  search: string;
  status: string;
  assignment: 'all' | 'assigned' | 'unassigned';
  dateRange: {
    from: string;
    to: string;
  };
}

interface CampaignFiltersProps {
  onFiltersChange: (filters: FilterState) => void;
  campaignCounts: {
    total: number;
    assigned: number;
    unassigned: number;
  };
  className?: string;
}

const defaultFilters: FilterState = {
  search: '',
  status: 'all',
  assignment: 'all',
  dateRange: {
    from: '',
    to: ''
  }
};

const CampaignFilters: React.FC<CampaignFiltersProps> = ({
  onFiltersChange,
  campaignCounts,
  className = ''
}) => {
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [searchInput, setSearchInput] = useState('');

  // Debounced search to avoid excessive API calls
  const debouncedSearch = debounce((searchTerm: string) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
  }, 300);

  // Handle search input changes
  useEffect(() => {
    debouncedSearch(searchInput);
  }, [searchInput, debouncedSearch]);

  // Notify parent component when filters change
  useEffect(() => {
    onFiltersChange(filters);
  }, [filters, onFiltersChange]);

  const handleFilterChange = (key: keyof FilterState, value: string | object) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDateRangeChange = (key: 'from' | 'to', value: string) => {
    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [key]: value
      }
    }));
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    setSearchInput('');
  };

  const hasActiveFilters = 
    filters.search !== '' ||
    filters.status !== 'all' ||
    filters.assignment !== 'all' ||
    filters.dateRange.from !== '' ||
    filters.dateRange.to !== '';

  return (
    <div className={`bg-white p-4 rounded-lg shadow border ${className}`}>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Campaign Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            Clear All Filters
          </button>
        )}
      </div>

      {/* Row 1: Search Bar (Full Width) */}
      <div className="mb-4">
        <label htmlFor="campaign-search" className="block text-sm font-medium text-gray-700 mb-1">
          Search Campaigns
        </label>
        <div className="relative">
          <input
            id="campaign-search"
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by campaign name or owner..."
            className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Row 2: Status and Assignment Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Status Filter */}
        <div>
          <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Campaign Status
          </label>
          <select
            id="status-filter"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="preparing">Preparing</option>
            <option value="running">Running</option>
            <option value="completed">Completed</option>
            <option value="paused">Paused</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Assignment Filter */}
        <div>
          <label htmlFor="assignment-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Assignment Status
          </label>
          <select
            id="assignment-filter"
            value={filters.assignment}
            onChange={(e) => handleFilterChange('assignment', e.target.value as 'all' | 'assigned' | 'unassigned')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Campaigns ({campaignCounts.total})</option>
            <option value="assigned">Assigned ({campaignCounts.assigned})</option>
            <option value="unassigned">Unassigned ({campaignCounts.unassigned})</option>
          </select>
        </div>
      </div>

      {/* Row 3: Date Range Filter */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Date Range (Created)
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label htmlFor="date-from" className="sr-only">From date</label>
            <input
              id="date-from"
              type="date"
              value={filters.dateRange.from}
              onChange={(e) => handleDateRangeChange('from', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="From date"
            />
          </div>
          <div>
            <label htmlFor="date-to" className="sr-only">To date</label>
            <input
              id="date-to"
              type="date"
              value={filters.dateRange.to}
              onChange={(e) => handleDateRangeChange('to', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="To date"
            />
          </div>
        </div>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {filters.search && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Search: {filters.search}
                <button
                  onClick={() => {
                    setFilters(prev => ({ ...prev, search: '' }));
                    setSearchInput('');
                  }}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.status !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Status: {filters.status}
                <button
                  onClick={() => handleFilterChange('status', 'all')}
                  className="ml-1 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.assignment !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Assignment: {filters.assignment}
                <button
                  onClick={() => handleFilterChange('assignment', 'all')}
                  className="ml-1 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            )}
            {(filters.dateRange.from || filters.dateRange.to) && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Date Range
                <button
                  onClick={() => handleFilterChange('dateRange', { from: '', to: '' })}
                  className="ml-1 text-yellow-600 hover:text-yellow-800"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignFilters;